<template>
  <div v-if="value && item" class="inv-overlay" @click.self="close">
    <div class="inv-modal inv-modal--xl">
      <!-- HEADER -->
      <div class="inv-modal__header">
        <div class="d-flex align-center">
          <v-icon small class="mr-2">mdi-cart-outline</v-icon>
          <span class="inv-title">
            #{{ item.sales_order?.order_number }} — {{ item.customer?.name }} {{ transactionCompleted === true ? '- COMPLETED' : '' }}
          </span>
        </div>

        <div class="d-flex align-center" style="gap:10px" v-if="!transactionCompleted">
          <v-btn v-if="!isEditing && checkifCompleted" class="inv-btn-v" color="success" @click="completeACare(item)">COMPLETE</v-btn>
          <v-btn v-if="!isEditing" class="inv-btn-v" @click="startEdit">EDIT</v-btn>
          <v-btn v-else class="inv-btn-v" @click="cancelEdit">Cancel</v-btn>
          <v-btn v-if="isEditing" color="primary" @click="save">Save</v-btn>

          <button class="inv-modal__close" @click="close">×</button>
        </div>
      </div>

      <!-- TABS -->
      <v-tabs v-model="tab" background-color="transparent" class="inv-tabs" grow>
        <v-tab>Details</v-tab>
        <v-tab>Order</v-tab>
        <v-tab>Customer</v-tab>
        <v-tab>Products</v-tab>
      </v-tabs>

      <div class="inv-modal__body">
        <v-tabs-items v-model="tab">
          <!-- TAB 1 -->
          <v-tab-item>
            <v-data-table
              :headers="kvHeaders"
              :items="baseRows"
              item-key="key"
              class="elevation-0 inv-kv-table"
              hide-default-footer
              disable-pagination
            >
              <template v-slot:item.value="{ item: row }">
                <!-- VIEW -->
                <div v-if="!isEditing || !row.editable" class="inv-kv-value" style="word-break: break-all;">
                  {{ displayRowValue(row) }}
                </div>
              
                <!-- EDIT -->
                <div v-else>
                  <v-select
                    v-if="row.type === 'select'"
                    v-model="form[row.key]"
                    :items="row.items"
                    item-text="text"
                    item-value="value"
                    :label="row.type_label"
                    dense
                    outlined
                    clearable
                    hide-details
                    :disabled="row.key === 'contact_status'"
                  />
                
                  <v-switch
                    v-else-if="row.type === 'switch'"
                    v-model="form[row.key]"
                    hide-details
                    class="mt-0"
                    disabled
                  />
                
                  <v-textarea
                    v-else-if="row.type === 'textarea'"
                    v-model="form[row.key]"
                    dense
                    outlined
                    auto-grow
                    rows="2"
                    hide-details
                  />

                  <v-text-field
                    v-else-if="row.type === 'assigned_to'"
                    v-model="form[row.key]"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>

                    
                  <div v-else-if="row.type === 'date'">
                    <b-form-datepicker
                      :id="`datepicker-${row.key}`"
                      v-model="form[row.key]"
                      calendar-width="100%"
                      locale="en"
                      start-weekday="1"
                      class="inv-input"
                      style="max-width: 260px"
                      :disabled="row.key !== 'call_date'"
                    ></b-form-datepicker>
                  </div>

                  <div v-else-if="row.type === 'callback_date'" class="d-flex gap-3">
                    <v-switch
                      v-model="need_callback"
                      hide-details
                      class="mt-0"
                      @change="form['callback_date'] = null"
                    />
                    <b-form-datepicker
                      v-if="need_callback"
                      :id="`datepicker-callback_date`"
                      v-model="form['callback_date']"
                      calendar-width="100%"
                      locale="en"
                      start-weekday="1"
                      class="inv-input"
                      style="max-width: 260px"
                      :disabled="!need_callback"
                    ></b-form-datepicker>
                  </div>
                
                  <v-text-field
                    v-else
                    v-model="form[row.key]"
                    dense
                    outlined
                    clearable
                    hide-details
                  />
                </div>
              </template>
            </v-data-table>
            <!-- RATING -->
              <google-rating-selector
                v-model="google_rating"
                @save="saveGoogleRating"
                @clear="clearGoogleRating"
              />
          </v-tab-item>

          <!-- TAB 2 -->
          <v-tab-item>
            <v-data-table
              :headers="kvHeaders"
              :items="orderRows"
              item-key="key"
              dense
              class="elevation-0 inv-kv-table"
              hide-default-footer
              disable-pagination
            >
              <template v-slot:item.value="{ item: row }">
                <div class="inv-kv-value">{{ row.value }}</div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- TAB 3 -->
          <v-tab-item>
            <v-data-table
              :headers="kvHeaders"
              :items="customerRows"
              item-key="key"
              dense
              class="elevation-0 inv-kv-table"
              hide-default-footer
              disable-pagination
            >
              <template v-slot:item.value="{ item: row }">
                <div class="inv-kv-value" style="white-space: pre-line;">
                  {{ row.value }}
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- TAB 4 -->
          <v-tab-item>
            <v-data-table
              :headers="productHeaders"
              :items="form.products"
              :items-per-page="10"
              class="elevation-0"
              dense
            >
              <template v-slot:item.name="{ item: p }">
                <div class="d-flex flex-column">
                  <strong>{{ p.name }}</strong>
                  <small class="text--secondary">{{ p.sku }}</small>
                </div>
              </template>

              <template v-slot:item.quantity="{ item: qty}">
                <div class="d-flex flex-column">
                  <strong>{{ qty.quantity }} {{ qty.quantity_unit }}</strong>
                  <small class="text--secondary">{{ qty.num_of_pcs }} db</small>
                </div>
              </template>

              <template v-slot:item.marketing_comments="{ item: p }">
                <div v-if="!isEditing">
                  {{ p.marketing_comments || '—' }}
                </div>

                <v-textarea
                  v-else
                  v-model="p.marketing_comments"
                  rows="2"
                  auto-grow
                  dense
                  outlined
                  hide-details
                  placeholder="Marketing comment..."
                  @blur="markAsChanged(p)"
                />
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import GoogleRatingSelector from './GoogleRatingSelector.vue';
import Swal from "sweetalert2";


export const ENDPOINT = "aftercare/";
export default {
  name: "AftercareViewDialog",
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: null },

    statusOptions: { type: Array, required: true },
    callStatusOptions: { type: Array, required: true },
    ratingOptions: { type: Array, required: true },
    satisfactionOptions: { type: Array, required: true },
    internalSatisfaction: { type: Array, required: true },
  },
  components: {GoogleRatingSelector},
  data() {
    return {
      tab: 0,
      isEditing: false,
      callDateMenu: false,
      form: {
        id: null,
        contact_status: null,
        call_status: null,
        call_date: null,
        customer_satisfaction: null,
        rating_request_decision: null,
        rating_request_sent: false,
        completed_at: null,
        assigned_to: null,
        general_notes: '',
        sales_notes: '',
        callback_date: null,
        internal_rating: null,
      
        sales_order: {},
        customer: {},
        products: [],
      },
      kvHeaders: [
        { text: 'Field', value: 'label', width: '200px' },
        { text: 'Value', value: 'value', sortable: false }
      ],
      productHeaders: [
        { text: "ID", value: "id" },
        { text: "REF", value: "sku" },
        { text: "PRODUCT", value: "name" },
        { text: "QTY", value: "quantity", width: 100 },
        { text: "COMMENT", value: "marketing_comments" },
      ],
      changedProductIds: new Set(),
      need_callback: false,
      google_rating: null,
    };
  },
  computed:{
    baseRows() {
      return [
        { key: "sales_notes", label: "Notes from Sales", type: "textarea", editable: false },
        { key: "contact_status", label: "Contact status", type: "select", type_label:'Select contact status', editable: true, items: this.statusOptions },
        { key: "call_status", label: "Call status", type: "select", type_label:'Select call status', editable: true, items: this.callStatusOptions },
        { key: "call_date", label: "Call date", type: "date", editable: true }, // YYYY-MM-DD
        { key: "callback_date", label: "Callback Date", type: "callback_date", editable: true }, // YYYY-MM-DD
        { key: "customer_satisfaction", label: "Satisfaction", type: "select", type_label:'Select satisfaction', editable: true, items: this.satisfactionOptions },
        { key: "rating_request_decision", label: "Rating decision", type: "select", type_label:'Select rating decision', editable: true, items: this.ratingOptions },
        { key: "rating_request_sent", label: "Rating sent", type: "switch", editable: true },
        { key: "internal_rating", label: "Internal Rating (Customer)", type: "select", type_label:'Select satisfaction', editable: true, items: this.internalSatisfaction  },
        { key: "completed_at", label: "Completed at", type: "date", editable: true },
        { key: "assigned_to", label: "Assigned to", type: "assigned_to", type_label:'Select one person', editable: true },
        { key: "general_notes", label: "Notes", type: "textarea", editable: true },
      ]
    },
    orderRows() {
      const so = (this.form && this.form.sales_order) ? this.form.sales_order : {}
      return [
        { key: "so_id", label: "Order ID", value: this.fallback(so.id) },
        { key: "so_number", label: "Order number", value: this.fallback(so.order_number) },
        { key: "so_order_date", label: "Order date", value: this.fallback(so.order_date) },
        { key: "so_delivery_date", label: "Delivery date", value: this.fallback(so.delivery_date) },
        { key: "so_total", label: "Total amount (net)", value: this.money(so.total_amount) + ' ' + so.currency},
        { key: "so_payment", label: "Payment status", value: this.fallback(so.payment_status) ? 'Paid' : 'Not yet' },
      ]
    },

    customerRows() {
      const cu = (this.form && this.form.customer) ? this.form.customer : {}
      return [
        { key: "cu_id", label: "Customer ID", value: this.fallback(cu.id) },
        { key: "cu_name", label: "Name", value: this.fallback(cu.name) },
        { key: "cu_email", label: "Email", value: this.fallback(cu.email) },
        { key: "cu_phone", label: "Phone", value: this.fallback(cu.phone) },
        { key: "cu_prev", label: "Previous orders", value: this.fallback(cu.previous_orders_count, 0) },
        { key: "cu_ship", label: "Shipping address", value: this.fallback(cu.shipping_address) },
        { key: "cu_bill", label: "Billing address", value: this.fallback(cu.billing_address) },
      ]
    },
    checkifCompleted(){
      if(this.form.call_status === 'not_contacted') return false;
      if(this.form.call_date === null) return false;
      if(this.form.rating_request_decision === 'not_decided') return false;

      return true;
    },
    transactionCompleted(){
      if(this.form.contact_status === 'completed' && this.form.completed_at !== null){
        return true;
      }
      return false;
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.tab = 0;
        this.isEditing = false;
        this.resetForm();
      }
    },
    item: {
      handler() {
        if (this.value) this.resetForm();
      },
      deep: true,
    },
    "form.call_status":{
      handler(v){
        if(v !== 'not_contacted'){
          this.form.call_date = new Date().toISOString().split('T')[0];
        }else{
          this.form.call_date = null;
        }
      }
    },
    'item.customer.google_rating': {
      immediate: true,
      handler(newVal) {
        this.google_rating = newVal;
      }
    }
  },
  methods: {
    markAsChanged(product){
      this.changedProductIds.add(product.id)
    },

    close() {
      this.$emit("input", false);
    },

    resetForm() {
      const cloned = JSON.parse(JSON.stringify(this.item || {}))
      if (!cloned.sales_order) cloned.sales_order = {}
      if (!cloned.customer) cloned.customer = {}
      if (!cloned.products) cloned.products = []
      this.form = cloned

      this.changedProductIds.clear();
    },

    fallback(v, fb = "—") {
      return (v === null || v === undefined || v === "") ? fb : v
    },

    startEdit() {
      this.isEditing = true;
      if(this.form.callback_date !== null){
        this.need_callback = true;
      }
      this.resetForm();
    },

    cancelEdit() {
      this.isEditing = false;
      this.resetForm();
    },

    async save() {
      const changedProducts = this.form.products.filter(p => this.changedProductIds.has(p.id));

      const payload = {
        id: this.form.id,
        contact_status: this.form.contact_status,
        call_status: this.form.call_status,
        call_date: this.form.call_date,
        callback_date: this.form.callback_date,
        customer_satisfaction: this.form.customer_satisfaction,
        general_notes: this.form.general_notes,
        rating_request_decision: this.form.rating_request_decision,
        rating_request_sent: this.form.rating_request_sent,
        internal_rating: this.form.internal_rating,
        completed_at: this.form.completed_at,
        //assigned_to: this.form.assigned_to,

        products: changedProducts,
      };

      this.$emit("save", payload);

      this.isEditing = false;
      this.changedProductIds.clear();
    },

    fmtDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d; // ha string (Y-m-d), hagyjuk
      return dt.toLocaleDateString("hu-HU");
    },

    money(v) {
      if (v === null || v === undefined || v === "") return "—";
      return new Intl.NumberFormat("hu-HU").format(Number(v));
    },

    callText(v) {
      const hit = (this.callStatusOptions || []).find(o => o.value === v);
      return hit ? hit.text : (v ?? "—");
    },

    statusText(v) {
      const hit = (this.statusOptions || []).find(o => o.value === v);
      return hit ? hit.text : (v ?? "—");
    },

    satisText(v) {
      const hit = (this.satisfactionOptions || []).find(o => o.value === v);
      return hit ? hit.text : (v ?? "Not yet rated");
    },

    internalSatisText(v) {
      const hit = (this.internalSatisfaction || []).find(o => o.value === v);
      return hit ? hit.text : (v ?? "The customer not yet rated");
    },

    ratingText(v) {
      const hit = (this.ratingOptions || []).find(o => o.value === v);
      return hit ? hit.text : (v ?? "—");
    },

    displayRowValue(row) {
      const v = this.form?.[row.key] ?? null

      if (row.key === "call_status") return this.callText(v)
      if (row.key === "contact_status") return this.statusText(v)
      if (row.key === "customer_satisfaction") return this.satisText(v)
      if (row.key === "rating_request_decision") return this.ratingText(v)
      if (row.key === "rating_request_sent") return v ? "Yes" : "No"
      if (row.key === "internal_rating") return this.internalSatisText(v)

      return this.fallback(v)
    },
    async completeACare(item){
      try{
        const response = await ApiService.post(ENDPOINT + `interactions/${item.id}/complete`, 
          {send_rating_request: item.rating_request_decision === 'no' ? false : true}
        );

        this.$emit('completed', 
        {
          data: response.data.data,
          message: 'Interaction completed successfully',
          type: 'success'
        });
    
      }catch(e){
        console.error(e);
        this.$emit('completed', {
          data: null,
          message: 'Error completing interaction',
          type: 'error'
        });
      }
    },
    async saveGoogleRating(rating) {
      try {
        const response = await ApiService.put(ENDPOINT + `interactions/${this.form.assigned_to}/google-rating`,
          { google_rating: rating }
        );

        console.log(response);
 
        this.google_rating = rating;
 
        /*this.$emit('rating-saved', {
          id: this.form.id,
          google_rating: rating
        });*/
 
        this.$emit('show-snackbar', 'Rating saved successfully', 'success');
 
      } catch (error) {
        console.error('Error saving rating:', error);
        this.$emit('show-snackbar', 'Error saving rating', 'error');
      }
    },
 
    async clearGoogleRating() {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You want to remove the rating.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });
    
      if (!result.isConfirmed) {
        this.$emit('show-snackbar', 'Rating clear cancelled', 'error');
        return;
      }
    
      try {
        await ApiService.put(
          ENDPOINT + `interactions/${this.form.assigned_to}/google-rating`
        );
      
        this.google_rating = null;
      
        // this.$emit('rating-cleared', { id: this.form.id });
        this.$emit('show-snackbar', 'Rating cleared', 'success');
      } catch (error) {
        console.error('Error clearing rating:', error);
        this.$emit('show-snackbar', 'Error clearing rating', 'error');
      }
    }
  },
};
</script>

<style scoped>
.inv-overlay {
  position: fixed; inset: 0; z-index: 999;
  background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(2px);
}
.inv-modal {
  background: var(--bg2);
  border: 1px solid var(--border2);
  box-shadow: 0 24px 64px rgba(0,0,0,0.15);
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
}
.inv-modal--xl { width: 1200px; max-width: 95vw; }

.inv-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border);
  background: var(--bg3);
}
.inv-title { font-family: 'IBM Plex Mono', monospace; font-weight: 700; letter-spacing: .06em; }

.inv-modal__close {
  background: none; border: none; color: var(--text-dim);
  font-size: 23px; cursor: pointer; line-height: 1; padding: 0 4px;
}
.inv-modal__body { padding: 14px 16px; overflow-y: auto; flex: 1; }

.inv-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.inv-detail-item--wide { grid-column: 1 / -1; }

.inv-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 10px; }

.inv-detail-item__label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px; font-weight: 700; letter-spacing: 0.14em;
  color: var(--text-dim); margin-bottom: 4px;
}
.inv-detail-item__value { font-size: 15px; color: var(--text-bright); }
.inv-detail-item__value--mono { font-family: 'IBM Plex Mono', monospace; color: var(--accent2); }

@media (max-width: 720px) {
  .inv-detail-grid, .inv-edit-grid { grid-template-columns: 1fr; }
}

.inv-kv-table >>> tbody tr {
  border-bottom: 1px solid var(--border) !important;
}

.inv-kv-table >>> tbody td {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

.inv-kv-table >>> .v-data-table tbody tr {
  height: 52px !important;
}

.inv-kv-table >>> .v-data-table tbody td {
  padding: 16px 8px !important;
}
</style>